/* CUSTOM: Pursed 4 Life site behavior — the prototype-v3 main.js + menu.js ported
   onto Horizon. One file, loaded site-wide from custom-header (defer, after the
   vendored GSAP / ScrollTrigger / Lenis assets).

   Horizon scroll model (see PORT-NOTES.md): desktop (>=990px) scrolls
   `.page-wrapper` (html/body are overflow:hidden), mobile scrolls the window.
   Every scroll-coupled feature here resolves the scroller first; ScrollTrigger
   gets it as its default scroller. Reduced motion: state toggles (header swap,
   menu open/close, view switches) still run; scrubs/reveals/smoothing do not.

   2026-07-29 rework (bug pass):
   · boot()/teardown() lifecycle — in the THEME EDITOR a section re-render
     replaces its DOM and silently kills every listener bound at load (the
     mobile burger went dead after any header edit). Shopify.designMode wires
     shopify:section:* events to a full teardown + re-boot.
   · Lenis is DESKTOP-ONLY — on mobile it never smoothed (native touch) but
     still cost a rAF loop every frame.
   · Reveals skip elements already in view at boot — gsap.from on visible
     content made the page blink when JS landed late on mobile.
   · Drop-gallery featuring gate is LIVE (matchMedia change), so an editor
     viewport switch can never strand desktop hover state at mobile widths.

   2026-07-29 (second bug pass — Faysal's mobile->desktop + lag reports):
   · The SCROLLER ITSELF is breakpoint-dependent (Horizon swaps it at 990px).
     boot() resolved it once, so crossing 990px without a reload stranded
     every ScrollTrigger on an element that no longer scrolls. Now re-boots
     on the crossing, carrying the scroll offset across the swap.
   · The editorial strip no longer pins/scrubs below 721px — on mobile it is
     the native swipe row it already fell back to without JS. */

(function () {
  'use strict';

  var docEl = document.documentElement;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasGsap = typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined';
  var desktopMQ = window.matchMedia('(min-width: 990px)');
  var firstBoot = true;
  var cleanups = [];

  function onCleanup(fn) { cleanups.push(fn); }
  function on(target, evName, fn, opts) {
    target.addEventListener(evName, fn, opts);
    onCleanup(function () { target.removeEventListener(evName, fn, opts); });
  }
  function observe(el, cb, opts) {
    var io = new IntersectionObserver(cb, opts);
    io.observe(el);
    onCleanup(function () { io.disconnect(); });
  }
  function onMQ(mq, fn) {
    if (!mq.addEventListener) return;
    mq.addEventListener('change', fn);
    onCleanup(function () { mq.removeEventListener('change', fn); });
  }
  function teardown() {
    cleanups.splice(0).reverse().forEach(function (fn) {
      try { fn(); } catch (e) { /* teardown never throws */ }
    });
  }

  function boot() {
    var pageWrapper = document.querySelector('.page-wrapper');
    var usePageWrapper = desktopMQ.matches && !!pageWrapper;

    /* ---------- header: transparent-over-hero -> fixed white (homepage only) ----------
       html.hdr-overlay is set by an inline script in custom-header before paint.
       State, not decoration — runs regardless of reduced motion (the CSS
       transition is what reduced-motion turns off). */
    var runway = document.querySelector('.p4l-runway');
    if (runway && docEl.classList.contains('hdr-overlay')) {
      observe(runway, function (entries) {
        docEl.classList.toggle('hdr-solid', !entries[0].isIntersecting);
      });
    }

    /* The burger now lives INSIDE .p4l-header (absolute) and tracks the header
       in every scroll state by construction — the abar-gone IO dance is gone
       (it desynced whenever the sticky abar sat subpixel-visible; Faysal's
       PDP report, 2026-07-29). */

    /* ---------- curved mobile menu (prototype js/menu.js, verbatim logic) ---------- */
    (function () {
      var toggle = document.querySelector('.p4l-cmenu-toggle');
      var panel = document.getElementById('p4l-cmenu');
      if (!toggle || !panel) return;

      docEl.classList.add('has-menu');

      var animate = typeof gsap !== 'undefined' && !reduceMotion;
      var path = panel.querySelector('.p4l-cmenu__curve path');
      var links = Array.prototype.slice.call(panel.querySelectorAll('.p4l-cmenu__link'));
      var isOpen = false;
      var tl = null;

      panel.setAttribute('tabindex', '-1');
      function focusPanel() { panel.focus({ preventScroll: true }); }

      var BULGE = 'M100 0 Q-60 50 100 100 Z';
      var FLAT = 'M100 0 Q100 50 100 100 Z';

      if (animate) {
        gsap.set(panel, { xPercent: 100, x: 100 });
        gsap.set(path, { attr: { d: BULGE } });
        gsap.set(links, { y: 44, opacity: 0 });
        tl = gsap.timeline({
          paused: true,
          onComplete: focusPanel,
          onReverseComplete: function () { panel.classList.remove('is-open'); }
        });
        tl.to(panel, { xPercent: 0, x: 0, duration: 0.8, ease: 'power4.inOut' }, 0)
          .to(path, { attr: { d: FLAT }, duration: 1.0, ease: 'power4.inOut' }, 0)
          .to(links, { y: 0, opacity: 1, duration: 0.55, ease: 'expo.out', stagger: 0.06 }, 0.35);
        onCleanup(function () {
          tl.kill();
          gsap.set(panel, { clearProps: 'transform' });
          gsap.set(links, { clearProps: 'transform,opacity' });
          if (path) path.setAttribute('d', FLAT);
        });
      }

      function onKey(e) { if (e.key === 'Escape') closeMenu(); }

      function openMenu() {
        isOpen = true;
        toggle.setAttribute('aria-expanded', 'true');
        panel.classList.add('is-open');
        docEl.classList.add('p4l-cmenu-lock');
        if (window.__lenis) window.__lenis.stop();
        document.addEventListener('keydown', onKey);
        if (tl) tl.play();
        else focusPanel();
      }

      function closeMenu() {
        if (!isOpen) return;
        isOpen = false;
        toggle.setAttribute('aria-expanded', 'false');
        docEl.classList.remove('p4l-cmenu-lock');
        if (window.__lenis) window.__lenis.start();
        document.removeEventListener('keydown', onKey);
        toggle.focus();
        if (tl) tl.reverse();
        else panel.classList.remove('is-open');
      }

      on(toggle, 'click', function () { isOpen ? closeMenu() : openMenu(); });
      var closeBtn = panel.querySelector('.p4l-cmenu__close');
      if (closeBtn) on(closeBtn, 'click', function () { closeMenu(); });
      links.forEach(function (link) {
        on(link, 'click', function () { closeMenu(); });
      });

      var mq = window.matchMedia('(min-width: 721px)');
      onMQ(mq, function (e) {
        if (e.matches && isOpen) {
          isOpen = false;
          toggle.setAttribute('aria-expanded', 'false');
          docEl.classList.remove('p4l-cmenu-lock');
          if (window.__lenis) window.__lenis.start();
          document.removeEventListener('keydown', onKey);
          if (tl) tl.pause(0);
          panel.classList.remove('is-open');
        }
      });

      /* re-boot mid-open (editor re-render): release the lock the new DOM
         can't know about */
      onCleanup(function () {
        document.removeEventListener('keydown', onKey);
        docEl.classList.remove('p4l-cmenu-lock');
        if (window.__lenis) window.__lenis.start();
      });
    })();

    /* ---------- drop gallery: static-partition featuring ----------
       Pure CSS :hover oscillates: the featured tile grows, neighbours compress,
       the tile under the cursor CHANGES, :hover re-targets, the row re-flows —
       a feedback loop that reads as flicker (Faysal, 2026-07-29). Fix: the
       pointer's x on a FIXED equal-slots map of the row decides the featured
       piece, so animated widths can never re-trigger the choice. Keyboard focus
       keeps parity. CSS :hover rules stay as the no-JS fallback (disabled here
       via the --js class); the gate is LIVE so featuring state can never
       survive into mobile widths. State, not decoration — runs under reduced
       motion. */
    (function () {
      var hgal = document.querySelector('.p4l-hgal');
      if (!hgal) return;
      var pieces = Array.prototype.slice.call(hgal.querySelectorAll('.p4l-hgal__piece'));
      if (pieces.length < 2) return;
      var gate = window.matchMedia('(hover: hover) and (min-width: 721px)');
      var current = -1;
      var setFeat = function (idx) {
        if (idx === current) return;
        current = idx;
        pieces.forEach(function (p, i) { p.classList.toggle('is-feat', i === idx); });
        hgal.classList.toggle('has-feat', idx > -1);
      };
      var applyGate = function (isDesktop) {
        hgal.classList.toggle('p4l-hgal--js', isDesktop);
        if (!isDesktop) setFeat(-1);
      };
      on(hgal, 'pointermove', function (e) {
        if (!gate.matches) return;
        var r = hgal.getBoundingClientRect();
        var idx = Math.floor(((e.clientX - r.left) / r.width) * pieces.length);
        setFeat(Math.max(0, Math.min(pieces.length - 1, idx)));
      });
      on(hgal, 'pointerleave', function () { if (gate.matches) setFeat(-1); });
      on(hgal, 'focusin', function (e) {
        if (!gate.matches) return;
        var p = e.target.closest('.p4l-hgal__piece');
        if (p) setFeat(pieces.indexOf(p));
      });
      on(hgal, 'focusout', function (e) {
        if (!gate.matches) return;
        if (!hgal.contains(e.relatedTarget)) setFeat(-1);
      });
      applyGate(gate.matches);
      onMQ(gate, function (e) { applyGate(e.matches); });
      onCleanup(function () {
        setFeat(-1);
        hgal.classList.remove('p4l-hgal--js');
      });
    })();

    /* ---------- catalogue view toggles (functionality — before the motion guard) ---------- */
    var cviews = document.querySelector('.p4l-cviews');
    var catalogSec = document.querySelector('.p4l-catalog[data-view]');
    if (cviews && catalogSec) {
      docEl.classList.add('has-cviews');
      on(cviews, 'click', function (e) {
        var btn = e.target.closest('[data-cview]');
        if (!btn) return;
        catalogSec.setAttribute('data-view', btn.getAttribute('data-cview'));
        cviews.querySelectorAll('[data-cview]').forEach(function (b) {
          b.setAttribute('aria-pressed', String(b === btn));
        });
        if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
      });
    }

    /* The archive's scroll-drawn SOLD strike was RETIRED 2026-07-29 (Faysal):
       sold pieces now read from a badge on a greyed plate, and the section
       needs no JS at all — see sections/custom-archive.liquid. */

    if (reduceMotion || !hasGsap) {
      /* everything stays visible; nothing else here needs GSAP */
      firstBoot = false;
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.defaults({ scroller: usePageWrapper ? pageWrapper : window });

    /* Pinned sections mis-measure when the browser restores a deep scroll
       position on reload — boot animated visits from the top. Only pages that
       PIN (strip/expand) need this; elsewhere native restoration is better UX.
       First boot only — an editor re-boot must never yank the preview to top. */
    if (firstBoot && 'scrollRestoration' in history && document.querySelector('.p4l-strip, .p4l-expand')) {
      history.scrollRestoration = 'manual';
      if (usePageWrapper) pageWrapper.scrollTo(0, 0);
      else window.scrollTo(0, 0);
    }
    firstBoot = false;

    /* Lenis smooth scroll — DESKTOP ONLY, wrapping the .page-wrapper scroller
       (PORT-NOTES gotcha: wrapping the wrong one no-ops). On mobile the window
       scrolls natively; Lenis added no smoothing there, just per-frame work. */
    if (typeof Lenis !== 'undefined' && usePageWrapper) {
      try {
        var lenis = new Lenis({ lerp: 0.11, wrapper: pageWrapper, content: pageWrapper });
        lenis.on('scroll', ScrollTrigger.update);
        var driveLenis = function (time) { lenis.raf(time * 1000); };
        gsap.ticker.add(driveLenis);
        gsap.ticker.lagSmoothing(0);
        window.__lenis = lenis; /* review loop uses lenis.scrollTo */
        onCleanup(function () {
          gsap.ticker.remove(driveLenis);
          lenis.destroy();
          if (window.__lenis === lenis) window.__lenis = undefined;
        });
      } catch (e) { /* smoothing is a nicety — never let it take scrolling down */ }
    }

    /* Generic reveals — settle up 24px, expo-out. gsap.from means no hidden
       initial state exists without JS. Elements already in view at boot are
       SKIPPED: on mobile this script can land 1s+ after first paint, and
       re-hiding content the user is reading reads as a glitch. */
    var viewH = usePageWrapper ? pageWrapper.clientHeight : window.innerHeight;
    var revealTweens = [];
    gsap.utils.toArray('[data-reveal]').forEach(function (el) {
      if (el.getBoundingClientRect().top < viewH * 0.85) return;
      revealTweens.push(gsap.from(el, {
        opacity: 0,
        y: 24,
        duration: 0.65,
        ease: 'expo.out',
        scrollTrigger: { trigger: el, start: 'top 85%', once: true }
      }));
    });
    onCleanup(function () {
      revealTweens.forEach(function (tw) {
        var targets = tw.targets();
        if (tw.scrollTrigger) tw.scrollTrigger.kill();
        tw.progress(1).kill();
        if (targets.length) gsap.set(targets, { clearProps: 'opacity,transform' });
      });
    });

    /* Pinned triggers in DOCUMENT ORDER (strip mid-page, expand below) or their
       start positions ignore each other's pin spacers. */

    /* Editorial strip: track translates horizontally while the section is pinned;
       vertical distance = the track's overflow width (1:1 scrub).
       DESKTOP ONLY (>=721px). Measured on a 375x750 DPR-3 touch profile, the
       mobile scrub cost far more than it bought: it scroll-jacked 3362px of a
       9003px page, it held the track (3737x406 CSS px = 11212px wide at DPR 3,
       past the max texture size of every phone GPU) permanently promoted, and
       ScrollTrigger's pin engage/release measured CLS 1.74 — two 0.869 jumps,
       the section visibly snapping as it pins and again as it lets go.
       Below 721px the section keeps the row it already falls back to with no
       JS: a native, compositor-driven horizontal swipe. Re-measured: CLS 0,
       page 9003px -> 5641px. */
    var strip = document.querySelector('.p4l-strip');
    var track = document.querySelector('.p4l-strip__track');
    if (strip && track) {
      var stripMM = gsap.matchMedia();
      stripMM.add('(min-width: 721px)', function () {
        strip.classList.add('is-scrub');
        var overflowX = function () {
          return Math.max(0, track.scrollWidth - docEl.clientWidth);
        };
        gsap.to(track, {
          x: function () { return -overflowX(); },
          ease: 'none',
          scrollTrigger: {
            trigger: strip, start: 'center center',
            end: function () { return '+=' + overflowX(); },
            scrub: true, pin: true, anticipatePin: 1, invalidateOnRefresh: true
          }
        });
        return function () {
          strip.classList.remove('is-scrub');
          gsap.set(track, { clearProps: 'transform' });
        };
      });
      on(window, 'load', function () { ScrollTrigger.refresh(); });
      onCleanup(function () { stripMM.revert(); });
    }

    /* About scroll-expansion: DOM defaults to the finished state; rewind + scrub.
       Mobile stage pins mid-viewport with a shorter scrub.
       `is-scrub` promotes .p4l-expand__media to its own compositor layer for
       the duration. Without it the mobile blur-fill (a blur(28px) ::before)
       sits inside a transform-scaled ancestor and Chrome re-paints and
       re-rasters the blur on EVERY scrub frame — measured at 375x750 DPR 3 /
       4x CPU: Paint x136 (26.3ms) + Raster 106ms + style recalc 129.8ms with
       the layer unpromoted, vs Paint x5 (3.3ms) + Raster 63ms + 7.0ms with it.
       That was the About-section lag. The class is only added when the scrub
       actually runs, so no-JS and reduced-motion never hold the layer. */
    var expand = document.querySelector('.p4l-expand');
    if (expand) {
      expand.classList.add('is-scrub');
      onCleanup(function () { expand.classList.remove('is-scrub'); });
      var mm = gsap.matchMedia();
      mm.add(
        { isMobile: '(max-width: 720px)', isDesktop: '(min-width: 721px)' },
        function (ctx) {
          var isMobile = ctx.conditions.isMobile;
          gsap.timeline({
            scrollTrigger: {
              trigger: expand,
              start: isMobile ? 'center center' : 'top top',
              end: isMobile ? '+=80%' : '+=110%',
              scrub: true, pin: true, anticipatePin: 1, invalidateOnRefresh: true
            }
          })
            .fromTo('.p4l-expand__media', { scale: 0.30 }, { scale: 1, ease: 'none' }, 0)
            .fromTo('.p4l-expand__eyebrow', { opacity: 1 }, { opacity: 0, ease: 'none' }, 0.05)
            .fromTo('.p4l-expand__scrim', { opacity: 0 }, { opacity: 1, ease: 'none' }, 0.55)
            .fromTo('.p4l-expand__quote', { opacity: 0, y: 28 }, { opacity: 1, y: 0, ease: 'none' }, 0.68);
        }
      );
      onCleanup(function () { mm.revert(); });
    }

  }

  boot();

  /* ---------- the scroller changes at 990px, so boot() has to as well ----------
     Horizon puts html/body at overflow:hidden >=990px and scrolls
     .page-wrapper instead; below 990px the window scrolls. boot() resolves
     that ONCE — into ScrollTrigger.defaults({scroller}) and into whether
     Lenis is built at all — so a viewport that CROSSES 990px without a
     reload leaves every trigger listening to an element that no longer
     scrolls. Faysal's report ("switching from mobile view to desktop bugs
     the storefront") is exactly this: verified at 375 -> 1280 with no
     reload, .p4l-hgal and the trust block stay at opacity 0 (their reveals
     can never fire), the strip's track never leaves x:0 and the About photo
     stays frozen at scale(0.3). The theme editor's viewport toggle does it,
     and so does any desktop window resize or tablet rotation.
     Same teardown + re-boot the editor already uses, plus the scroll offset
     carried across the swap — the browser zeroes whichever scroller it just
     retired, which on its own dumps the preview back at the top. */
  var lastScrollY = 0;
  window.addEventListener('scroll', function () {
    var pw = document.querySelector('.page-wrapper');
    lastScrollY = (desktopMQ.matches && pw) ? pw.scrollTop : window.scrollY;
  }, { passive: true, capture: true });

  /* Moves the scroll offset onto whichever element scrolls at this width, and
     ZEROES the one that just stopped scrolling. That second half matters:
     Chrome does NOT reset the root's scroll offset when html flips to
     overflow:hidden, it just stops responding to the wheel — so a page
     scrolled to 1800 on mobile keeps documentElement.scrollTop === 1800
     after the switch and it STACKS on the wrapper's offset. Measured on the
     shipped build: wrapper 1500 + root 1800 = 3300px of visual scroll while
     every measurement believed 1500, which is why the switched-to-desktop
     page renders blank bands and mis-placed pins.
     (behavior:'instant' because html and .page-wrapper both carry
     scroll-behavior:smooth — an animated restore fights the re-measure.) */
  function setScroll(y) {
    var pw = document.querySelector('.page-wrapper');
    var useWrapper = desktopMQ.matches && !!pw;
    if (pw) pw.scrollTo({ top: useWrapper ? y : 0, behavior: 'instant' });
    window.scrollTo({ top: useWrapper ? 0 : y, behavior: 'instant' });
  }

  /* NOT via onMQ() — this listener must outlive the teardown it triggers */
  if (desktopMQ.addEventListener) {
    desktopMQ.addEventListener('change', function () {
      var y = lastScrollY;
      teardown();
      setScroll(y);   /* boot() skips reveals already in view — measure at the real position */
      boot();
      setScroll(y);   /* pin spacers rebuilt underneath us — land where we started */
      if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
    });
  }

  /* THEME EDITOR ONLY: a section re-render replaces its DOM, killing every
     listener bound at load (the burger went dead after any header edit).
     Debounced full teardown + re-boot against the fresh DOM. Never runs on
     the storefront — Shopify.designMode is editor-exclusive. */
  if (window.Shopify && Shopify.designMode) {
    var rebootTimer;
    ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(function (evName) {
      document.addEventListener(evName, function () {
        clearTimeout(rebootTimer);
        rebootTimer = setTimeout(function () { teardown(); boot(); }, 80);
      });
    });
  }
})();
